<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div class="d-flex align-center">
                    <h1 class="text-left">Users</h1>
                    <v-card class="ml-6" color="surface-light" style="min-width: 300px;">
                        <v-card-text class="pa-2">
                            <v-text-field
                                v-model="quickSearch"
                                append-inner-icon="mdi-magnify"
                                autofocus
                                clearable
                                density="compact"
                                hide-details
                                label="Quick Search"
                                single-line
                                variant="solo"
                                @click:clear="()=>{quickSearch=null;currentPage=1;fetchUsers()}"
                                @keydown.enter="applyQuickSearch"
                                @click:append-inner="applyQuickSearch"
                            ></v-text-field>
                        </v-card-text>
                    </v-card>
                </div>
                <div>
                    <v-btn color="black" prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add</v-btn>
                    <v-btn
                        v-if="authUser.type !== 'admin' && authUser.role.toLowerCase().includes('admin')"
                        :disabled="inviteUserDialogSwitch"
                        class="mx-3"
                        color="black"
                        @click="inviteUserDialogSwitch = true">Invite
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="authUser.super" cols="12" md="3" sm="6">
                <v-select v-model="userType" :items="userTypes" clearable
                          label="User Type" @update:model-value="()=>{ currentPage=1;fetchUsers() }"/>
            </v-col>
            <v-col v-if="authUser.super" cols="12" md="3" sm="6" class="d-flex align-center">
                <v-switch v-model="showDeleted" color="error" density="compact" hide-details
                          :label="showDeleted ? 'Hide Deleted Users' : 'Show Deleted Users'"
                          @update:model-value="()=>{ currentPage=1;fetchUsers() }"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th-btn :sorted-column="sortedColumn??null" column="first_name"
                                sortable title="First Name" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="last_name"
                                sortable title="Last Name" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="type"
                                sortable title="Type" @sort="sort"/>
                        <th v-if="authUser.type !== 'admin'">Role</th>
                        <th-btn :sorted-column="sortedColumn??null" column="email"
                                sortable title="Email" @sort="sort"/>
                        <th>API Key</th>
                        <th-btn :sorted-column="sortedColumn??''" column="contact_phone"
                                sortable title="Contact Phone" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="status"
                                sortable title="Status" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="created_at"
                                sortable title="Created At" @sort="sort"/>
                        <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="user in users" v-if="!loading && users.length" :key="user.id"
                        :class="['text-left', user.deleted_at ? 'bg-red-lighten-5 text-red-darken-1' : '']">
                        <td>
                            <UserAvatar :src="user.avatar" :first-name="user.first_name"
                                        :last-name="user.last_name" :email="user.email" size="24"/>
                        </td>
                        <td>{{ user.first_name }}</td>
                        <td>{{ user.last_name }}</td>
                        <td>{{ capitalize(user.type) }}</td>
                        <td v-if="authUser.type !== 'admin'">{{ formatRole(user.role) }}</td>
                        <td>{{ user.email }}</td>
                        <td>{{ user.pivot?.api_key ?? '' }}</td>
                        <td>{{ user.contact_phone }}</td>
                        <td class="text-capitalize">{{ user.status }}</td>
                        <td>{{ formatDateTime(user.created_at) }}</td>
                        <td>
                            <div class="d-flex align-center ga-1">
                                <NoteIcon v-if="authUser.super" entity-type="users" :entity-id="user.id" :note-count="user.notes_count ?? 0"/>
                                <v-btn-group density="compact" divided variant="outlined">
                                <v-btn v-if="authUser.type ==='admin'" :disabled="rolesDialogSwitch"
                                       class="text-info"
                                       size="x-small"
                                       @click="showRoles(user)">Roles
                                </v-btn>
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editUser(user)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteUser(user)">Delete
                                </v-btn>
                                <v-menu v-if="authUser.type ==='admin'">
                                    <template v-slot:activator="{ props }">
                                        <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text"></v-btn>
                                    </template>

                                    <v-list density="compact">
                                        <v-list-item title="Signed Docs" @click="showSignedDocs(user)"/>
                                        <v-list-item title="Active Sessions" @click="showActiveSessions(user)"/>
                                        <v-list-item title="Reset Password" @click="openResetPassword(user)"/>
                                        <v-list-item class="text-error" title="End All Sessions"
                                                     @click="forceLogout(user)"/>
                                        <template v-if="authUser.super">
                                            <v-divider class="my-1"/>
                                            <v-list-item prepend-icon="mdi-shield-lock"
                                                         title="IP Restrictions"
                                                         @click="manageIpRestrictions(user)"/>
                                            <v-list-item prepend-icon="mdi-cellphone"
                                                         title="App Installs"
                                                         :to="{name: 'admin-app-installs', query: {user_id: user.id}}"/>
                                        </template>
                                        <template v-if="authUser.super && user.deleted_at">
                                            <v-divider class="my-1"/>
                                            <v-list-item class="text-error" prepend-icon="mdi-delete-forever"
                                                         title="Permanently Delete" @click="forceDeleteUser(user)"/>
                                        </template>
                                    </v-list>
                                </v-menu>
                            </v-btn-group>
                            </div>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" :colspan="authUser.type !== 'admin' ? 11 : 10">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else :colspan="authUser.type !== 'admin' ? 11 : 10">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @click="fetchUsers"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Create-User v-if="createDialogSwitch" v-model="createDialogSwitch" @addUser="addUser"/>
    <Edit-User v-if="editDialogSwitch" v-model="editDialogSwitch" :user="selectedUser"
               @updateUser="updateUser"/>
    <roles-dialogue v-if="rolesDialogSwitch" v-model="rolesDialogSwitch" :user="selectedUser"/>
    <legal-documents v-if="docsDialogSwitch" v-model="docsDialogSwitch" :user="selectedUser"/>
    <active-sessions v-if="sessionDialogSwitch" v-model="sessionDialogSwitch" :user="selectedUser"/>

    <Invite-User v-if="authUser.type !== 'admin' && inviteUserDialogSwitch"
                 v-model="inviteUserDialogSwitch"
                 :company="authUser.company"
                 :roles="roles"
                 @update-invited-user=""
    />

    <!-- Reset Password Dialog -->
    <v-dialog v-model="resetPasswordDialog" max-width="450" persistent>
        <v-card>
            <v-card-title>Reset Password</v-card-title>
            <v-card-subtitle v-if="resetPasswordUser" class="pb-0">
                {{ resetPasswordUser.first_name }} {{ resetPasswordUser.last_name }} ({{ resetPasswordUser.email }})
            </v-card-subtitle>
            <v-card-text>
                <v-radio-group v-model="resetPasswordMode" class="mb-2">
                    <v-radio label="Generate random password" value="random"/>
                    <v-radio label="Set specific password" value="manual"/>
                </v-radio-group>
                <v-text-field v-if="resetPasswordMode === 'manual'" v-model="manualPassword"
                              :rules="[vRules.required, vRules.minLength(8)]"
                              autocomplete="new-password" label="New Password" type="password" density="compact"/>
                <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                    The new password will be emailed to the user. They will be required to change it on first login.
                </v-alert>
            </v-card-text>
            <v-card-actions class="d-flex justify-end px-4 pb-4">
                <v-btn @click="resetPasswordDialog = false" variant="text">Cancel</v-btn>
                <v-btn color="primary" :loading="resetPasswordLoading"
                       :disabled="resetPasswordMode === 'manual' && (!manualPassword || manualPassword.length < 8)"
                       @click="submitResetPassword">Reset Password
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {capitalize, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import EditUser from "@/components/dialogs/users/Edit.vue";
import CreateUser from "@/components/dialogs/users/Create.vue";
import rolesDialogue from "@/components/dialogs/users/Roles.vue";
import InviteUser from "@/components/dialogs/companies/Users/Invite.vue";
import ActiveSessions from "@/components/dialogs/users/ActiveSessions.vue";
import LegalDocuments from "@/components/dialogs/users/LegalDocuments.vue";
import NoteIcon from "@/components/dialogs/notes/NoteIcon.vue";
import UserAvatar from "@/components/UserAvatar.vue";

const users = ref([]);
const roles = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref(null);
const sortedColumn = ref(null);
const authUser = useAuthStore().user;
const {showSnackbar} = useSnackbarStore();
const router = useRouter();
const docsDialogSwitch = ref(false);
const sessionDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const inviteUserDialogSwitch = ref(false);
const editDialogSwitch = ref(false);
const rolesDialogSwitch = ref(false);
const selectedUser = ref(null);
const userType = ref();
const showDeleted = ref(false);
const quickSearch = ref(null);
const resetPasswordDialog = ref(false);
const resetPasswordUser = ref(null);
const resetPasswordMode = ref('random');
const resetPasswordLoading = ref(false);
const manualPassword = ref(null);
// The user type filter is super admin only, both in the UI and on the
// API server side. Hidden via v-if="authUser.super" in the template; the
// listing endpoint also ignores the type param for non super callers.
// Admin is included so super admins can narrow the global table to
// regular admin rows; without this option they show up in the
// unfiltered list but cannot be isolated.
const userTypes = [
    {value: 'super_admin', title: 'Super User (SAS)'},
    {value: 'admin', title: 'Admin'},
    {value: 'api', title: 'API Users'},
    {value: 'company', title: 'Company Users'},
];

onMounted(() => {
    fetchUsers();
    if (authUser.type !== 'admin') {
        fetchRoles();
    }
});

const addUser = (user) => {
    users.value.unshift(user);
}
const editUser = (user) => {
    selectedUser.value = user;
    editDialogSwitch.value = true;
}
const showRoles = (user) => {
    selectedUser.value = user;
    rolesDialogSwitch.value = true;
}
const showSignedDocs = (user) => {
    selectedUser.value = user;
    docsDialogSwitch.value = true;
}
const showActiveSessions = (user) => {
    selectedUser.value = user;
    sessionDialogSwitch.value = true;
}
const manageIpRestrictions = (user) => {
    router.push({name: 'user-ip-restrictions', params: {userId: user.id}});
}
const updateUser = (user) => {
    users.value.map((comp, index) => {
        if (comp.id === user.id) {
            users.value[index] = user;
        }
    });
}

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchUsers();
}
const applyQuickSearch = () => {
    currentPage.value = 1;
    fetchUsers();
};
const fetchRoles = () => {
    send('get', `roles?type=${authUser.type}`)
        .then((response) => {
            roles.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Roles could not be loaded', 'error')
        })
}
const fetchUsers = () => {
    loading.value = true;
    const data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        type: userType.value ?? '',
        quick_search: quickSearch.value,
        show_deleted: showDeleted.value ? 1 : '',
    }
    send('get', `users`
        , data)
        .then((response) => {
            users.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Users could not be loaded', 'error')
        })
}
const deleteUser = (user) => {
    if (confirm(
        `Are you sure to delete "${user.name}" user?`
    )) {
        send('delete', `users/${user.id}`)
            .then((response) => {
                if (response.data.success) {
                    users.value.map((comp, index) => {
                        if (comp.id === user.id) {
                            users.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(
                    error.response?.data?.message ?? `"${user.first_name}" User could not be deleted`
                    , 'error')
            })
    }
}
const openResetPassword = (user) => {
    resetPasswordUser.value = user;
    resetPasswordMode.value = 'random';
    manualPassword.value = null;
    resetPasswordDialog.value = true;
}
const submitResetPassword = () => {
    resetPasswordLoading.value = true;
    const payload = resetPasswordMode.value === 'manual' ? {password: manualPassword.value} : {};
    send('put', `users/${resetPasswordUser.value.id}/admin-reset-password`, payload)
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                resetPasswordDialog.value = false;
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Failed to reset password', 'error');
        })
        .finally(() => resetPasswordLoading.value = false);
}
const formatRole = (role) => {
    if (!role) return '';
    // Strip portal prefix - e.g. "Commercial Fleet & Asset Tracking Driver" → "Driver"
    const parts = role.split(' ');
    const lastWord = parts[parts.length - 1];
    if (['Admin', 'Manager', 'User', 'Driver'].includes(lastWord)) return lastWord;
    return role;
}
const forceLogout = (user) => {
    if (confirm(
        `Are you sure to End All Sessions of user "${user.name}"?`
    )) {
        send('post', `users/${user.id}/end-sessions`
        )
            .then((response) => {
                if (response.data.success) {
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(
                    `"${user.first_name}" User's sessions could not ended'`, 'error')
            })
    }
}

const forceDeleteUser = (user) => {
    if (confirm(`PERMANENTLY delete user "${user.email}"?\n\nThis cannot be undone. All user data will be removed.`)) {
        send('delete', `users/${user.id}/force-delete`)
            .then((response) => {
                if (response.data.success) {
                    showSnackbar(response.data.message, 'success');
                    fetchUsers();
                }
            })
            .catch((error) => {
                showSnackbar(error.response?.data?.message || 'Failed to delete user', 'error');
            });
    }
}
</script>

<style scoped>
</style>
